import { CONFIG, TILE } from '../config/tuning'
import type { LevelData } from './level'

export enum Tile {
  Void = 0,
  Floor = 1,
  Wall = 2,
  Door = 3,
  LockedDoor = 4,
  Exit = 5,
  Destructible = 6,
  Trap = 7,
  Tele1 = 8,
  Tele2 = 9,
  Tele3 = 10,
  Tele4 = 11,
  Rubble = 12,
  OpenDoor = 13,
  /** circuit relay plate — latching all of them on a floor retracts every shutter */
  Relay = 14,
  RelayOn = 15,
  Shutter = 16,
  ShutterOpen = 17
}

const CODE_TO_TILE: Record<string, Tile> = {
  ' ': Tile.Void,
  '.': Tile.Floor,
  '#': Tile.Wall,
  D: Tile.Door,
  L: Tile.LockedDoor,
  E: Tile.Exit,
  X: Tile.Destructible,
  T: Tile.Trap,
  '1': Tile.Tele1,
  '2': Tile.Tele2,
  '3': Tile.Tele3,
  '4': Tile.Tele4,
  R: Tile.Relay,
  G: Tile.Shutter
}

export function isSolid(t: Tile): boolean {
  return t === Tile.Wall || t === Tile.Void || t === Tile.Door || t === Tile.LockedDoor || t === Tile.Destructible || t === Tile.Shutter
}

export function isTeleporter(t: Tile): boolean {
  return t >= Tile.Tele1 && t <= Tile.Tele4
}

export class TileMap {
  readonly w: number
  readonly h: number
  private grid: Uint8Array
  private destructibleHp: Map<number, number> = new Map()

  constructor(level: LevelData, destructibleWallHp = CONFIG.hazards.destructibleWallHp) {
    this.h = level.tiles.length
    this.w = level.tiles[0]!.length
    this.grid = new Uint8Array(this.w * this.h)
    for (let y = 0; y < this.h; y++) {
      const row = level.tiles[y]!
      for (let x = 0; x < this.w; x++) {
        const t = CODE_TO_TILE[row[x]!] ?? Tile.Floor
        this.grid[y * this.w + x] = t
        if (t === Tile.Destructible) this.destructibleHp.set(y * this.w + x, destructibleWallHp)
      }
    }
  }

  at(tx: number, ty: number): Tile {
    if (tx < 0 || ty < 0 || tx >= this.w || ty >= this.h) return Tile.Void
    return this.grid[ty * this.w + tx] as Tile
  }

  set(tx: number, ty: number, t: Tile): void {
    if (tx < 0 || ty < 0 || tx >= this.w || ty >= this.h) return
    this.grid[ty * this.w + tx] = t
  }

  solidAt(tx: number, ty: number): boolean {
    return isSolid(this.at(tx, ty))
  }

  /** damage a destructible wall; returns true when it crumbles */
  damageWall(tx: number, ty: number): boolean {
    const key = ty * this.w + tx
    const hp = this.destructibleHp.get(key)
    if (hp === undefined) return false
    if (hp <= 1) {
      this.destructibleHp.delete(key)
      this.set(tx, ty, Tile.Rubble)
      return true
    }
    this.destructibleHp.set(key, hp - 1)
    return false
  }

  openDoor(tx: number, ty: number): void {
    this.set(tx, ty, Tile.OpenDoor)
  }

  /** the paired pad for a teleporter tile, if any */
  telePartner(tx: number, ty: number): { x: number; y: number } | null {
    const t = this.at(tx, ty)
    if (!isTeleporter(t)) return null
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        if ((x !== tx || y !== ty) && this.at(x, y) === t) return { x, y }
      }
    }
    return null
  }

  *tilesOfType(t: Tile): Generator<{ x: number; y: number }> {
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        if (this.at(x, y) === t) yield { x, y }
      }
    }
  }

  /**
   * Move a circle through the grid with axis-separated collision resolution.
   * Returns tile types touched (for doors/traps/teleporters/exit handling).
   */
  moveCircle(e: { x: number; y: number; radius: number }, dx: number, dy: number, clip: boolean): void {
    if (!clip) {
      e.x += dx
      e.y += dy
      // even non-clipped actors stay inside the outer bounds
      e.x = Math.max(e.radius, Math.min(this.w * TILE - e.radius, e.x))
      e.y = Math.max(e.radius, Math.min(this.h * TILE - e.radius, e.y))
      return
    }
    this.sweepAxis(e, dx, 0)
    this.sweepAxis(e, 0, dy)
  }

  private sweepAxis(e: { x: number; y: number; radius: number }, dx: number, dy: number): void {
    e.x += dx
    e.y += dy
    const r = e.radius
    const minTx = Math.floor((e.x - r) / TILE)
    const maxTx = Math.floor((e.x + r) / TILE)
    const minTy = Math.floor((e.y - r) / TILE)
    const maxTy = Math.floor((e.y + r) / TILE)
    for (let ty = minTy; ty <= maxTy; ty++) {
      for (let tx = minTx; tx <= maxTx; tx++) {
        if (!this.solidAt(tx, ty)) continue
        // closest point on tile AABB to circle center
        const cx = Math.max(tx * TILE, Math.min((tx + 1) * TILE, e.x))
        const cy = Math.max(ty * TILE, Math.min((ty + 1) * TILE, e.y))
        const ddx = e.x - cx
        const ddy = e.y - cy
        const d2 = ddx * ddx + ddy * ddy
        if (d2 >= r * r) continue
        // corner contact (closest point off-center on both axes) resolves
        // radially — wall corners act rounded, so actors slide around them
        // instead of catching; face contact keeps the flat axis clamp
        const corner = cx !== e.x && cy !== e.y
        if (dx !== 0 && !corner) e.x = dx > 0 ? tx * TILE - r : (tx + 1) * TILE + r
        else if (dy !== 0 && !corner) e.y = dy > 0 ? ty * TILE - r : (ty + 1) * TILE + r
        else {
          // corner graze, or static overlap (e.g. pushed inside): radial pushout
          const d = Math.sqrt(d2) || 1
          e.x = cx + (ddx / d) * r
          e.y = cy + (ddy / d) * r
        }
      }
    }
  }

  /**
   * Push a circle out of any solid tiles it overlaps without moving it
   * otherwise — for position writes that bypass moveCircle (crowd separation).
   */
  resolveCircle(e: { x: number; y: number; radius: number }): void {
    this.sweepAxis(e, 0, 0)
  }

  /** line-of-sight between two world points (for ranged AI) */
  lineOfSight(x0: number, y0: number, x1: number, y1: number): boolean {
    const steps = Math.ceil(Math.hypot(x1 - x0, y1 - y0) / (TILE / 2))
    for (let i = 1; i < steps; i++) {
      const t = i / steps
      const x = x0 + (x1 - x0) * t
      const y = y0 + (y1 - y0) * t
      if (this.solidAt(Math.floor(x / TILE), Math.floor(y / TILE))) return false
    }
    return true
  }
}
